import { useQuery } from '@tanstack/react-query';

import { Payment } from '@/types/payment';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface StripeSubscription extends Payment {
  stripe_subscription_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

const METHOD = 'GET';
const ENDPOINT = 'payments/subscriptions';

export type ApiResponse_GetStripeSubscriptions = StripeSubscription[];

export interface UseGetStripeSubscriptionsParams {
  tenant_id?: string;
}

// Define a separate query key for subscriptions endpoint
export const QUERY_KEY = (params?: UseGetStripeSubscriptionsParams) =>
  getQueryKey(METHOD, ENDPOINT, params || {});

export const useGetStripeSubscriptions = (
  params?: UseGetStripeSubscriptionsParams,
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    queryKey: QUERY_KEY(params),
    enabled: enabled && !!session?.access_token,
    queryFn: async () => {
      try {
        const data = await handleApiCall<
          ApiResponse_GetStripeSubscriptions,
          UseGetStripeSubscriptionsParams
        >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), params);
        return data;
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          const paymentsResponse = await handleApiCall<Payment[]>(
            session,
            'GET',
            getResolvedEndpoint('payments', {})
          );

          const stripeSubscriptions: StripeSubscription[] = paymentsResponse
            .filter(
              payment =>
                payment.isSubscription &&
                payment.status === 'active' &&
                payment.type === 'rent' &&
                payment.subscriptionId
            )
            .map(payment => ({
              ...payment,
              stripe_subscription_id: payment.subscriptionId || '',
              current_period_start: payment.date,
              current_period_end: payment.period || '',
              cancel_at_period_end: false,
            }));

          return stripeSubscriptions;
        }
        throw error;
      }
    },
  });
};
